'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { usePathname } from 'next/navigation';
import type { TickerItem } from '@/components/layout/activity-ticker';

type DemoActivityContextValue = {
  /** Synthetic ticker items for coin demo route (newest first) */
  items: TickerItem[];
  /** Increments when items change — use as effect dependency */
  version: number;
  pushItem: (item: TickerItem) => void;
  clear: () => void;
};

const DemoActivityContext = createContext<DemoActivityContextValue | null>(null);

const COIN_DEMO_PATH = /^\/coin\/0x[a-fA-F0-9]{40}\/demo$/;

export function DemoActivityProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<TickerItem[]>([]);
  const [version, setVersion] = useState(0);
  const pathname = usePathname();
  const isCoinDemo = pathname ? COIN_DEMO_PATH.test(pathname) : false;

  useEffect(() => {
    if (!isCoinDemo) {
      setItems([]);
      setVersion((v) => v + 1);
    }
  }, [isCoinDemo]);

  const pushItem = useCallback((item: TickerItem) => {
    setItems((prev) => [item, ...prev].slice(0, 50));
    setVersion((v) => v + 1);
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    setVersion((v) => v + 1);
  }, []);

  const value = useMemo(
    () => ({ items, version, pushItem, clear }),
    [items, version, pushItem, clear],
  );

  return (
    <DemoActivityContext.Provider value={value}>{children}</DemoActivityContext.Provider>
  );
}

export function useDemoActivityOptional(): DemoActivityContextValue | null {
  return useContext(DemoActivityContext);
}
